import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import { useAuth } from "../../hooks/useAuth";
import { BIBLE_BOOKS } from "../../mocks/bibleBooks";
import { DEFAULT_ACCENT } from "../../hooks/useReaderSettings";

interface Note {
  id: string;
  userId: string;
  bookId: number;
  bookName: string;
  chapter: number;
  verseNum: number | null;
  noteText: string;
  translation: string;
  createdAt: string;
  updatedAt: string;
}

const STORAGE_KEY = "applesmp_notes";

function generateId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getAllNotes(): Note[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
  } catch {
    return [];
  }
}

function saveAllNotes(notes: Note[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(notes));
}

export default function NotesPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const accentColor = localStorage.getItem("applecore_accent_color") ?? DEFAULT_ACCENT;

  const urlBookId = searchParams.get("bookId") ? Number(searchParams.get("bookId")) : 40;
  const urlBookName = searchParams.get("bookName") ?? "Matthew";
  const urlChapter = searchParams.get("chapter") ? Number(searchParams.get("chapter")) : 1;
  const urlVerse = searchParams.get("verse") ? Number(searchParams.get("verse")) : null;
  const urlTranslation = searchParams.get("translation") ?? "KJV";

  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(!!searchParams.get("bookId"));
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [formBookId, setFormBookId] = useState(urlBookId);
  const [formChapter, setFormChapter] = useState(urlChapter);
  const [formVerse, setFormVerse] = useState<number | "">(urlVerse ?? "");
  const [formText, setFormText] = useState("");
  const [filterBook, setFilterBook] = useState("all");

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    const userNotes = getAllNotes()
      .filter((n) => n.userId === user.id)
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    setNotes(userNotes);
    setLoading(false);
  }, [user, authLoading, navigate]);

  const selectedBook = BIBLE_BOOKS.find((b) => b.id === formBookId);
  const chapterCount = selectedBook?.chapters ?? 1;

  function handleSave() {
    if (!user || !formText.trim()) return;
    setSaving(true);

    if (editingNote) {
      const all = getAllNotes();
      const idx = all.findIndex((n) => n.id === editingNote.id);
      if (idx !== -1) {
        all[idx] = { ...all[idx], noteText: formText.trim(), updatedAt: new Date().toISOString() };
        saveAllNotes(all);
        setNotes(
          all
            .filter((n) => n.userId === user.id)
            .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
        );
      }
      setEditingNote(null);
    } else {
      const bookName = BIBLE_BOOKS.find((b) => b.id === formBookId)?.name ?? urlBookName;
      const newNote: Note = {
        id: generateId(),
        userId: user.id,
        bookId: formBookId,
        bookName,
        chapter: formChapter,
        verseNum: formVerse !== "" ? formVerse : null,
        noteText: formText.trim(),
        translation: urlTranslation,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const all = [newNote, ...getAllNotes()];
      saveAllNotes(all);
      setNotes(all.filter((n) => n.userId === user.id));
    }

    setFormText("");
    setShowForm(false);
    setSaving(false);
  }

  function startEdit(note: Note) {
    setEditingNote(note);
    setFormBookId(note.bookId);
    setFormChapter(note.chapter);
    setFormVerse(note.verseNum ?? "");
    setFormText(note.noteText);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function cancelForm() {
    setShowForm(false);
    setEditingNote(null);
    setFormText("");
    setFormBookId(urlBookId);
    setFormChapter(urlChapter);
    setFormVerse(urlVerse ?? "");
  }

  function handleDelete(id: string) {
    setDeletingId(id);
    const all = getAllNotes().filter((n) => n.id !== id);
    saveAllNotes(all);
    setNotes((prev) => prev.filter((n) => n.id !== id));
    setDeletingId(null);
  }

  const allBooks = [...new Set(notes.map((n) => n.bookName))];
  const filteredNotes = filterBook === "all" ? notes : notes.filter((n) => n.bookName === filterBook);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 md:px-6 pt-24 pb-20">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">My Notes</h1>
            <p className="text-[#555] text-sm">
              Personal reflections and notes on scripture — stored on this device.
            </p>
          </div>
          <button
            onClick={() => { cancelForm(); setShowForm(true); }}
            className="flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: accentColor, color: "white" }}
          >
            <i className="ri-add-line" />
            New Note
          </button>
        </div>

        {/* Add / Edit Form */}
        {showForm && (
          <div className="bg-[#111] border border-[#2e2e2e] rounded-2xl p-5 mb-8">
            <h3 className="text-white text-sm font-semibold mb-4">
              {editingNote ? "Edit Note" : "Add Note"}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="text-[#555] text-xs uppercase tracking-widest block mb-1.5">Book</label>
                <select
                  value={formBookId}
                  onChange={(e) => { setFormBookId(Number(e.target.value)); setFormChapter(1); }}
                  className="w-full bg-[#1a1a1a] border border-[#2e2e2e] text-white text-sm rounded-lg px-3 py-2 cursor-pointer focus:outline-none"
                  disabled={!!editingNote}
                >
                  {BIBLE_BOOKS.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[#555] text-xs uppercase tracking-widest block mb-1.5">Chapter</label>
                <select
                  value={formChapter}
                  onChange={(e) => setFormChapter(Number(e.target.value))}
                  className="w-full bg-[#1a1a1a] border border-[#2e2e2e] text-white text-sm rounded-lg px-3 py-2 cursor-pointer focus:outline-none"
                  disabled={!!editingNote}
                >
                  {Array.from({ length: chapterCount }, (_, i) => i + 1).map((ch) => (
                    <option key={ch} value={ch}>Chapter {ch}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[#555] text-xs uppercase tracking-widest block mb-1.5">Verse (optional)</label>
                <input
                  type="number"
                  min={1}
                  max={200}
                  value={formVerse}
                  onChange={(e) => setFormVerse(e.target.value ? Number(e.target.value) : "")}
                  placeholder="e.g. 3"
                  className="w-full bg-[#1a1a1a] border border-[#2e2e2e] text-white text-sm rounded-lg px-3 py-2 focus:outline-none placeholder:text-[#333]"
                  disabled={!!editingNote}
                />
              </div>
            </div>

            <textarea
              value={formText}
              onChange={(e) => setFormText(e.target.value.slice(0, 2000))}
              placeholder="Write your note, reflection, or observation here..."
              rows={5}
              className="w-full bg-[#1a1a1a] border border-[#2e2e2e] text-white text-sm rounded-lg px-4 py-3 focus:outline-none placeholder:text-[#333] resize-none mb-3"
            />
            <div className="flex items-center justify-between">
              <span className="text-[#333] text-xs">{formText.length}/2000</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={cancelForm}
                  className="px-4 py-2 rounded-lg text-sm text-[#666] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={!formText.trim() || saving}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  style={{ backgroundColor: accentColor, color: "white" }}
                >
                  {saving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
                  {editingNote ? "Save Changes" : "Save Note"}
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div
              className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: `${accentColor} transparent transparent transparent` }}
            />
          </div>
        ) : notes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-[#111] border border-[#1e1e1e] rounded-2xl">
              <i className="ri-file-text-line text-2xl text-[#444]" />
            </div>
            <h3 className="text-white text-lg font-semibold">No notes yet</h3>
            <p className="text-[#555] text-sm max-w-sm">
              While reading, click the pencil icon next to any verse to start a note.
              Or click <strong className="text-white">New Note</strong> above.
            </p>
            <Link
              to="/scripture"
              className="mt-2 flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: accentColor, color: "white" }}
            >
              <i className="ri-book-open-line" />
              Open Bible Reader
            </Link>
          </div>
        ) : (
          <>
            {allBooks.length > 1 && (
              <div className="flex items-center gap-2 mb-5 flex-wrap">
                <select
                  value={filterBook}
                  onChange={(e) => setFilterBook(e.target.value)}
                  className="bg-[#111] border border-[#1e1e1e] text-white text-sm rounded-lg px-3 py-2 cursor-pointer focus:outline-none"
                >
                  <option value="all">All Books ({notes.length})</option>
                  {allBooks.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="space-y-3">
              {filteredNotes.map((note) => (
                <div
                  key={note.id}
                  className="group bg-[#111] border border-[#1e1e1e] rounded-xl p-5 hover:border-[#2e2e2e] transition-colors"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <span className="text-xs font-medium" style={{ color: accentColor }}>
                        {note.bookName} {note.chapter}
                        {note.verseNum ? `:${note.verseNum}` : ""}
                      </span>
                      <span className="text-[#333] text-xs ml-2">{note.translation}</span>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <button
                        onClick={() => startEdit(note)}
                        title="Edit note"
                        className="w-7 h-7 flex items-center justify-center rounded-md bg-[#1a1a1a] border border-[#2e2e2e] text-[#666] hover:text-white transition-colors cursor-pointer"
                      >
                        <i className="ri-pencil-line text-xs" />
                      </button>
                      <button
                        onClick={() => handleDelete(note.id)}
                        disabled={deletingId === note.id}
                        title="Delete note"
                        className="w-7 h-7 flex items-center justify-center rounded-md bg-[#1a1a1a] border border-[#2e2e2e] text-[#666] hover:text-red-400 transition-colors cursor-pointer"
                      >
                        {deletingId === note.id ? (
                          <i className="ri-loader-4-line animate-spin text-xs" />
                        ) : (
                          <i className="ri-delete-bin-line text-xs" />
                        )}
                      </button>
                    </div>
                  </div>
                  <p className="text-[#ccc] text-sm leading-relaxed whitespace-pre-wrap">{note.noteText}</p>
                  <p className="text-[#333] text-xs mt-3">
                    {new Date(note.updatedAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
